/**
 * @fileoverview O visual do assistente da IA: a mesma paleta das telas de
 * entrada (monocromática; cor só para o que deu certo ou errado), cartões com
 * borda fina, a barra dos passos e o único movimento que chama atenção, o
 * preparo da base. Respeita a redução de movimento.
 */

import { makeStyles } from "@material-ui/core/styles";

import { dialogShell } from "../../styles/dialogShell";

export const useAiSetupStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  const text = dark ? "#FAFAFA" : "#09090B";
  const muted = dark ? "#A1A1AA" : "#52525B";
  const faint = "#71717A";
  const line = dark ? "rgba(255,255,255,0.08)" : "#E4E4E7";
  const card = dark ? "#141416" : "#FFFFFF";
  const tint = dark ? "rgba(255,255,255,0.04)" : "#F4F4F5";
  const ok = dark ? "#4ADE80" : "#15803D";
  const focus = dark ? "rgba(91,124,255,0.6)" : "rgba(61,92,253,0.55)";
  const reduced = "@media (prefers-reduced-motion: reduce)";
  return {
    root: { width: "100%", color: text },
    view: { animation: "$viewIn 240ms ease-out", [reduced]: { animation: "none" } },
    head: { maxWidth: 720, marginBottom: 28 },
    stepLabel: {
      display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 12, rowGap: 6, fontSize: 13, fontWeight: 600, color: muted, marginBottom: 14,
      "& > span": { whiteSpace: "nowrap" },
    },
    stepBar: { display: "flex", gap: 6, flex: "0 0 132px" },
    stepSegment: {
      flex: 1, height: 4, borderRadius: 999, background: line, overflow: "hidden", position: "relative",
      "&::after": {
        content: '""', position: "absolute", inset: 0, borderRadius: 999, background: text,
        transform: "scaleX(0)", transformOrigin: "left", transition: "transform 420ms ease-out",
        [reduced]: { transition: "none" },
      },
    },
    stepSegmentOn: { "&::after": { transform: "scaleX(1)" } },
    title: { margin: 0, fontSize: 28, fontWeight: 700, letterSpacing: "-0.6px", lineHeight: 1.2, color: text, textWrap: "balance" },
    subtitle: { margin: "10px 0 0", fontSize: 15, lineHeight: 1.6, color: muted, maxWidth: 600 },

    // Passo 1: o formulário e, ao lado, o que a IA aprende
    sourceGrid: {
      display: "grid", gridTemplateColumns: "minmax(0, 1.1fr) minmax(0, 0.9fr)", gap: 20, alignItems: "start",
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
    },
    card: { background: card, border: `1px solid ${line}`, borderRadius: 14, padding: 24, [theme.breakpoints.down("xs")]: { padding: 18 } },
    cardTitle: { display: "flex", alignItems: "center", gap: 10, margin: "0 0 4px", fontSize: 16, fontWeight: 650, color: text },
    cardText: { margin: "0 0 18px", fontSize: 14, lineHeight: 1.55, color: muted },
    iconBox: {
      width: 34, height: 34, borderRadius: 9, flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center",
      background: tint, color: text, border: `1px solid ${line}`,
    },
    inputIcon: { position: "absolute", left: 13, top: "50%", transform: "translateY(-50%)", color: faint, pointerEvents: "none" },
    inputWithIcon: { paddingLeft: "40px !important" },
    textarea: { minHeight: 150, resize: "vertical", lineHeight: 1.55, paddingTop: 12, paddingBottom: 12, fontFamily: "inherit" },
    divider: {
      display: "flex", alignItems: "center", gap: 12, margin: "18px 0", fontSize: 12.5, color: faint,
      "&::before, &::after": { content: '""', flex: 1, height: 1, background: line },
    },
    textButton: {
      display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 12px", borderRadius: 8, border: `1px solid ${line}`,
      background: "transparent", color: text, fontSize: 13.5, fontWeight: 500, cursor: "pointer", fontFamily: "inherit",
      "&:hover": { background: tint },
      "&:focus-visible": { outline: `1px solid ${focus}`, outlineOffset: 2 },
    },
    aside: { background: tint, border: `1px solid ${line}`, borderRadius: 14, padding: 22 },
    asideTitle: { margin: "0 0 14px", fontSize: 14, fontWeight: 650, color: text },
    learnList: { listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 12 },
    learnItem: { display: "flex", gap: 12, alignItems: "flex-start" },
    learnName: { display: "block", fontSize: 13.5, fontWeight: 600, color: text },
    learnText: { display: "block", fontSize: 12.5, lineHeight: 1.5, color: muted, marginTop: 1 },
    footNote: { display: "flex", alignItems: "center", gap: 8, marginTop: 16, fontSize: 12.5, color: faint },
    skipRow: { marginTop: 22 },

    // Passo 2: o preparo
    preparing: { maxWidth: 640 },
    stageList: { listStyle: "none", margin: "4px 0 0", padding: 0 },
    stage: {
      display: "flex", alignItems: "center", gap: 12, padding: "13px 0", borderBottom: `1px solid ${line}`, fontSize: 14, color: faint,
      transition: "color 240ms ease-out", "&:last-child": { borderBottom: "none" },
    },
    stageActive: { color: text, fontWeight: 600 },
    stageDone: { color: text },
    stageMark: {
      width: 22, height: 22, borderRadius: "50%", flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center",
      border: `1.5px solid ${line}`, color: "transparent", transition: "all 240ms ease-out",
    },
    stageMarkActive: {
      borderColor: text, borderTopColor: "transparent", animation: "$spin 900ms linear infinite",
      [reduced]: { animation: "none", borderTopColor: text },
    },
    stageMarkDone: { borderColor: ok, background: ok, color: dark ? "#052E16" : "#FFFFFF", animation: "$pop 260ms ease-out", [reduced]: { animation: "none" } },
    stageStatus: { marginLeft: "auto", fontSize: 12, fontWeight: 500, color: faint },
    progress: { height: 4, borderRadius: 999, background: line, overflow: "hidden", margin: "18px 0 10px" },
    progressFill: { height: "100%", background: text, borderRadius: 999, transition: "width 600ms ease-out", [reduced]: { transition: "none" } },
    progressRow: { display: "flex", justifyContent: "space-between", fontSize: 12.5, color: faint },

    // O estado vazio e os botões em linha
    learnedEmpty: { margin: 0, fontSize: 13.5, color: faint },
    actions: { display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginTop: 26, maxWidth: 920 },
    primaryInline: { width: "auto !important", minWidth: 180, padding: "0 22px !important", marginTop: "0 !important" },
    secondaryInline: { width: "auto !important", padding: "0 18px !important", marginTop: "0 !important" },

    // As janelas (base e teste): a moldura compartilhada e o corpo daqui.
    ...dialogShell(theme, { width: 760 }),
    dialogBody: { padding: "16px 22px", display: "flex", flexDirection: "column", gap: 12, maxHeight: "calc(100vh - 240px)", overflowY: "auto" },
    knowledgeArea: { minHeight: 320, resize: "vertical", lineHeight: 1.6, fontSize: 13.5, fontFamily: "inherit", padding: 14 },
    counter: { alignSelf: "flex-end", fontSize: 12, color: faint },
    pages: { background: tint, border: `1px solid ${line}`, borderRadius: 10, padding: "12px 14px" },
    pagesTitle: { margin: "0 0 6px", fontSize: 12.5, fontWeight: 650, color: text },
    pageLink: { display: "block", fontSize: 12.5, lineHeight: 1.7, color: `${muted} !important`, wordBreak: "break-all", textDecoration: "none", "&:hover": { textDecoration: "underline", color: `${text} !important` } },

    chatBody: { display: "flex", flexDirection: "column", minHeight: 300, maxHeight: "min(560px, calc(100vh - 220px))", padding: 0 },
    chatScroll: { flex: 1, overflowY: "auto", padding: "16px 22px", display: "flex", flexDirection: "column", gap: 10 },
    chip: {
      padding: "8px 12px", borderRadius: 999, border: `1px solid ${line}`, background: card, color: text, fontSize: 13, cursor: "pointer", fontFamily: "inherit",
      "&:hover": { background: tint },
      "&:focus-visible": { outline: `1px solid ${focus}`, outlineOffset: 2 },
    },
    bubble: { maxWidth: "80%", padding: "9px 12px", borderRadius: 12, fontSize: 14, lineHeight: 1.5, whiteSpace: "pre-wrap", animation: "$bubbleIn 220ms ease-out", [reduced]: { animation: "none" } },
    fromCustomer: { alignSelf: "flex-end", background: dark ? "#2E3038" : "#18181B", color: "#FAFAFA", borderBottomRightRadius: 4 },
    fromAi: { alignSelf: "flex-start", background: tint, border: `1px solid ${line}`, color: text, borderBottomLeftRadius: 4 },
    bubbleError: { borderColor: dark ? "rgba(239,68,68,0.3)" : "#FECACA", color: dark ? "#FCA5A5" : "#B91C1C" },
    thinking: { alignSelf: "flex-start", fontSize: 13, color: faint, display: "flex", alignItems: "center", gap: 8 },
    chatInputRow: { display: "flex", gap: 8, padding: "12px 22px 16px", borderTop: `1px solid ${line}` },
    sendButton: { width: "44px !important", minWidth: 44, padding: "0 !important", marginTop: "0 !important", flexShrink: 0 },

    // Passo 3: a conquista, a descoberta, o primeiro teste e a ativação
    stepDone: { display: "inline-flex", alignItems: "center", gap: 6, color: ok, fontWeight: 600 },
    hero: { display: "flex", alignItems: "flex-start", gap: 18, maxWidth: 920, marginBottom: 28, [theme.breakpoints.down("xs")]: { flexDirection: "column", gap: 14 } },
    heroIcon: {
      position: "relative", width: 52, height: 52, flexShrink: 0, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
      background: dark ? "rgba(74,222,128,0.12)" : "#DCFCE7", color: ok,
    },
    heroIconCelebrate: {
      animation: "$pop 520ms cubic-bezier(.2,.9,.3,1.3) both",
      "&::before": {
        content: '""', position: "absolute", inset: -4, borderRadius: "50%", border: `2px solid ${ok}`, opacity: 0,
        animation: "$ring 900ms ease-out 180ms",
      },
      "& $burst": { animation: "$burst 760ms ease-out 160ms both" },
      [reduced]: { animation: "none", "&::before": { animation: "none" }, "& $burst": { animation: "none", opacity: 0 } },
    },
    burst: {
      position: "absolute", top: "50%", left: "50%", width: 5, height: 5, marginLeft: -2.5, marginTop: -2.5, borderRadius: "50%",
      background: ok, opacity: 0, pointerEvents: "none",
    },
    heroTitle: { margin: 0, fontSize: 28, fontWeight: 700, letterSpacing: "-0.6px", lineHeight: 1.22, color: text, textWrap: "balance", [theme.breakpoints.down("xs")]: { fontSize: 24 } },
    heroText: { margin: "8px 0 0", fontSize: 15, lineHeight: 1.6, color: muted, maxWidth: 620, textWrap: "pretty" },

    learnedLayout: {
      display: "grid", gridTemplateColumns: "minmax(0, 1.15fr) minmax(0, 0.85fr)", gap: 20, alignItems: "start", maxWidth: 1040,
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
    },
    cardHead: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 6, flexWrap: "wrap" },
    linkButton: {
      display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 8px", marginRight: -8, borderRadius: 8, border: "none", background: "transparent",
      color: muted, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
      "&:hover": { color: text, background: tint },
      "&:focus-visible": { outline: `1px solid ${focus}`, outlineOffset: 1 },
    },
    finding: {
      display: "grid", gridTemplateColumns: "32px minmax(0, 1fr)", gap: 12, padding: "14px 0", borderTop: `1px solid ${line}`,
      "&:first-of-type": { borderTop: "none" },
    },
    findingEnter: { animation: "$viewIn 320ms ease-out both", [reduced]: { animation: "none" } },
    findingIcon: {
      width: 32, height: 32, borderRadius: 9, display: "inline-flex", alignItems: "center", justifyContent: "center",
      background: tint, border: `1px solid ${line}`, color: text,
    },
    findingLabel: { display: "block", fontSize: 12.5, fontWeight: 650, color: muted, marginBottom: 5 },
    findingText: { margin: 0, fontSize: 14.5, lineHeight: 1.55, color: text },
    chipRow: { display: "flex", flexWrap: "wrap", gap: 6 },
    tag: {
      display: "inline-flex", alignItems: "center", padding: "4px 10px", borderRadius: 999, fontSize: 13, lineHeight: 1.45, color: text,
      background: tint, border: `1px solid ${line}`,
    },
    factList: { listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 7 },
    fact: { display: "flex", alignItems: "flex-start", gap: 8, fontSize: 14, lineHeight: 1.5, color: text, "& svg": { flexShrink: 0, marginTop: 3, color: muted } },
    gap: {
      display: "flex", alignItems: "flex-start", gap: 10, marginTop: 14, padding: "12px 14px", borderRadius: 10,
      background: tint, border: `1px dashed ${dark ? "rgba(255,255,255,0.16)" : "#D4D4D8"}`, fontSize: 13.5, lineHeight: 1.5, color: muted,
      "& svg": { flexShrink: 0, marginTop: 2, color: text },
    },
    gapAction: { display: "block", marginTop: 6 },

    // No computador, fica à vista enquanto a pessoa lê o que a IA aprendeu.
    meet: {
      background: card, border: `1px solid ${line}`, borderRadius: 14, padding: 22,
      [theme.breakpoints.up("md")]: { position: "sticky", top: 16 },
      [theme.breakpoints.down("xs")]: { padding: 18 },
    },
    meetTitle: { margin: "0 0 14px", fontSize: 16, fontWeight: 650, color: text },
    nameRow: { display: "flex", alignItems: "center", gap: 10, marginBottom: 14 },
    nameLabel: { fontSize: 12.5, fontWeight: 650, color: muted, whiteSpace: "nowrap" },
    nameInput: { height: "36px !important", fontSize: "14px !important", padding: "0 10px !important" },
    greetingRow: { display: "flex", alignItems: "flex-end", gap: 10, marginBottom: 14 },
    aiAvatar: {
      width: 30, height: 30, borderRadius: "50%", flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center",
      background: dark ? "#2E3038" : "#18181B", color: "#FAFAFA", fontSize: 12, fontWeight: 700,
    },
    greeting: {
      padding: "10px 13px", borderRadius: 14, borderBottomLeftRadius: 4, background: tint, border: `1px solid ${line}`,
      fontSize: 14, lineHeight: 1.55, color: text, animation: "$bubbleIn 360ms ease-out 240ms both", [reduced]: { animation: "none" },
    },
    askLabel: { display: "block", fontSize: 12.5, fontWeight: 650, color: muted, margin: "2px 0 8px" },
    questionList: { display: "grid", gap: 8 },
    question: {
      display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, width: "100%", padding: "10px 12px", borderRadius: 10,
      border: `1px solid ${line}`, background: card, color: text, fontSize: 13.5, textAlign: "left", cursor: "pointer", fontFamily: "inherit",
      transition: "background 160ms ease-out, border-color 160ms ease-out",
      "& svg": { flexShrink: 0, color: muted, transition: "transform 160ms ease-out" },
      "&:hover:not(:disabled)": { background: tint, "& svg": { transform: "translateX(2px)", color: text } },
      "&:focus-visible": { outline: `1px solid ${focus}`, outlineOffset: 1 },
      "&:disabled": { opacity: 0.55, cursor: "default" },
    },
    preparingAgent: { display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: faint, marginTop: 10 },

    activation: {
      display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", gap: 20, alignItems: "center", maxWidth: 1040, marginTop: 20,
      padding: 22, borderRadius: 14, background: tint, border: `1px solid ${line}`,
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
    },
    activationTitle: { margin: "0 0 6px", fontSize: 16, fontWeight: 650, color: text },
    activationList: { listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 4 },
    activationItem: { display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13.5, lineHeight: 1.55, color: muted, "& svg": { flexShrink: 0, marginTop: 3, color: text } },
    activationActions: { display: "flex", flexDirection: "column", alignItems: "stretch", gap: 8, minWidth: 240, [theme.breakpoints.down("sm")]: { minWidth: 0 } },
    activationPrimary: { marginTop: "0 !important", height: "48px !important", fontSize: "15px !important" },
    activationSecondary: { marginTop: "0 !important" },
    activationNote: { fontSize: 12, color: faint, textAlign: "center" },

    // A conversa de teste: começa pela saudação e pelas perguntas, sem uma área vazia
    chatGreetingRow: { display: "flex", alignItems: "flex-end", gap: 8, alignSelf: "flex-start", maxWidth: "88%" },
    chatSuggestions: { display: "flex", flexWrap: "wrap", gap: 8, marginTop: 4, paddingLeft: 38 },
    praise: {
      alignSelf: "center", display: "inline-flex", alignItems: "center", gap: 7, margin: "4px 0", padding: "6px 12px", borderRadius: 999,
      fontSize: 12.5, fontWeight: 600, color: ok, background: dark ? "rgba(74,222,128,0.10)" : "#F0FDF4",
      border: `1px solid ${dark ? "rgba(74,222,128,0.22)" : "#BBF7D0"}`, animation: "$bubbleIn 320ms ease-out both", [reduced]: { animation: "none" },
    },
    chatFoot: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "0 22px 14px", fontSize: 12.5, color: faint },

    "@keyframes ring": { "0%": { opacity: 0.6, transform: "scale(0.9)" }, "100%": { opacity: 0, transform: "scale(1.5)" } },
    "@keyframes burst": {
      "0%": { opacity: 0, transform: "rotate(var(--angle)) translateY(0) scale(0.6)" },
      "30%": { opacity: 1 },
      "100%": { opacity: 0, transform: "rotate(var(--angle)) translateY(-38px) scale(1)" },
    },
    "@keyframes viewIn": { from: { opacity: 0, transform: "translateY(6px)" }, to: { opacity: 1, transform: "none" } },
    "@keyframes spin": { to: { transform: "rotate(360deg)" } },
    "@keyframes pop": { "0%": { transform: "scale(0.6)", opacity: 0 }, "100%": { transform: "scale(1)", opacity: 1 } },
    "@keyframes bubbleIn": { from: { opacity: 0, transform: "translateY(4px)" }, to: { opacity: 1, transform: "none" } },
  };
});
